import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/ui/useDebounce';
import type { MonthlyCut } from '@/api/domains/attendance';


const AUTO_SEARCH_DEBOUNCE_MS = 300;

export interface UseMonthlyCutAutoSelectionOptions {
  monthlyCutsWithinRange: MonthlyCut[];
  loadingMonthlyCuts: boolean;
  /** Scope key (e.g. selected company); a change re-fires the search for the same monthlyCutId. */
  scopeKey: string;
  /**
   * Cuando es `false`, la auto-selección no sincroniza ni dispara la búsqueda: la pantalla ya tiene
   * un corte definido por otra vía (p.ej. filtros preaplicados por querystring) y la selección
   * automática lo sobrescribiría. Por defecto `true`.
   */
  enabled?: boolean;
  onSync: (cut: MonthlyCut) => void;
  onSearch: (monthlyCutId: number) => void;
}

export interface UseMonthlyCutAutoSelectionResult {
  hasUserSelectedCut: boolean;
  markUserSelectedCut: () => void;
  resetAutoSelection: () => void;
}

export const useMonthlyCutAutoSelection = (
  year: number | undefined,
  month: number | undefined,
  monthlyCutId: number | undefined,
  options: UseMonthlyCutAutoSelectionOptions,
): UseMonthlyCutAutoSelectionResult => {
  const {
    monthlyCutsWithinRange,
    loadingMonthlyCuts,
    scopeKey,
    enabled = true,
    onSync,
    onSearch,
  } = options;

  const [hasUserSelectedCut, setHasUserSelectedCut] = useState(false);

  const markUserSelectedCut = useCallback(() => setHasUserSelectedCut(true), []);
  const resetAutoSelection = useCallback(() => setHasUserSelectedCut(false), []);

  const resolvedCut = useMemo(() => {
    if (monthlyCutsWithinRange.length === 0) return null;

    if (!hasUserSelectedCut) return monthlyCutsWithinRange[0];

    if (monthlyCutId) {
      const selected = monthlyCutsWithinRange.find((cut) => cut.monthlyCutId === monthlyCutId);
      if (selected) return selected;
    }

    const matchingCuts = monthlyCutsWithinRange.filter((cut) => {
      if (year && cut.year !== year) return false;
      if (month && cut.month !== month) return false;
      return true;
    });

    return matchingCuts[0] ?? monthlyCutsWithinRange[0];
  }, [monthlyCutsWithinRange, hasUserSelectedCut, monthlyCutId, year, month]);

  useEffect(() => {
    if (!enabled) return;
    if (loadingMonthlyCuts) return;
    if (!resolvedCut || resolvedCut.year === null || resolvedCut.month === null) return;
    if (
      year === resolvedCut.year &&
      month === resolvedCut.month &&
      monthlyCutId === resolvedCut.monthlyCutId
    ) {
      return;
    }

    onSync(resolvedCut);
  }, [enabled, resolvedCut, loadingMonthlyCuts, year, month, monthlyCutId, onSync]);

  const debouncedMonthlyCutId = useDebounce(monthlyCutId, AUTO_SEARCH_DEBOUNCE_MS);
  const lastSearchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!debouncedMonthlyCutId) return;

    const searchKey = `${scopeKey}|${debouncedMonthlyCutId}`;
    if (lastSearchedKeyRef.current === searchKey) return;

    lastSearchedKeyRef.current = searchKey;
    onSearch(debouncedMonthlyCutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, debouncedMonthlyCutId, scopeKey]);

  return { hasUserSelectedCut, markUserSelectedCut, resetAutoSelection };
};

export default useMonthlyCutAutoSelection;
